import { useState, useEffect, useRef, useCallback } from 'react';

export type LocationPrecision = 'exact' | 'approximate' | 'hidden';

export interface LocationState {
  lat: number | null;
  lng: number | null;
  heading: number;
  isMoving: boolean;
  error: string | null;
  granted: boolean;
}

function fuzz(coord: number, meters = 150): number {
  const deg = meters / 111_000;
  return Math.round(coord / deg) * deg;
}

const LOCATION_DEBOUNCE_MS = 2000;
const MOVEMENT_SPEED_THRESHOLD = 1.5; // m/s — avoids GPS noise false positives

/** Quick IP-based city-level location — no permission required, ~city accuracy.
 *  Used only as an initial map center fallback until GPS kicks in. */
async function getIpLocation(): Promise<{ lat: number; lng: number } | null> {
  try {
    const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: AbortSignal.timeout(4000) });
    if (!res.ok) return null;
    const d = await res.json();
    const lat = parseFloat(d.latitude);
    const lng = parseFloat(d.longitude);
    if (isNaN(lat) || isNaN(lng)) return null;
    return { lat, lng };
  } catch { return null; }
}

export function useLocation(precision: LocationPrecision) {
  const [state, setState] = useState<LocationState>({
    lat: null, lng: null, heading: 0, isMoving: false, error: null, granted: false,
  });
  const prevRef = useRef<{ lat: number; lng: number; ts: number } | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const gpsReceivedRef = useRef(false);

  const applyPrecision = useCallback(
    (lat: number, lng: number): [number | null, number | null] => {
      if (precision === 'hidden') return [null, null];
      if (precision === 'approximate') return [fuzz(lat), fuzz(lng)];
      return [lat, lng];
    },
    [precision]
  );

  // ── IP fallback: fires immediately, gives a city-level center while GPS loads ──
  useEffect(() => {
    if (precision === 'hidden') return;
    getIpLocation().then(pos => {
      if (!pos || gpsReceivedRef.current) return; // GPS already came in, skip
      setState(s => {
        if (s.lat !== null) return s; // already have a real location
        return { ...s, lat: pos.lat, lng: pos.lng };
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GPS: high-accuracy, overrides IP location once granted ──
  useEffect(() => {
    if (!navigator.geolocation) {
      setState(s => ({ ...s, error: 'Geolocation not supported' }));
      return;
    }
    if (precision === 'hidden') {
      setState(s => ({ ...s, lat: null, lng: null, granted: true }));
      return;
    }

    const id = navigator.geolocation.watchPosition(
      pos => {
        const now = Date.now();
        // Debounce: skip updates that come too fast
        if (now - lastUpdateRef.current < LOCATION_DEBOUNCE_MS) return;
        lastUpdateRef.current = now;
        gpsReceivedRef.current = true;

        const { latitude: lat, longitude: lng } = pos.coords;
        const [pLat, pLng] = applyPrecision(lat, lng);
        let isMoving = false;
        let heading = 0;

        if (prevRef.current) {
          const dt = (now - prevRef.current.ts) / 1000;
          if (dt > 0) {
            const dlat = lat - prevRef.current.lat;
            const dlng = (lng - prevRef.current.lng) * Math.cos((lat * Math.PI) / 180);
            const distM = Math.sqrt(dlat * dlat + dlng * dlng) * 111_000;
            const speed = distM / dt;
            isMoving = speed > MOVEMENT_SPEED_THRESHOLD;
            if (isMoving) heading = (Math.atan2(dlng, dlat) * 180) / Math.PI;
          }
        }

        prevRef.current = { lat, lng, ts: now };
        setState({ lat: pLat, lng: pLng, heading, isMoving, error: null, granted: true });
      },
      err => setState(s => ({ ...s, error: err.message, granted: false })),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    return () => navigator.geolocation.clearWatch(id);
  }, [precision, applyPrecision]);

  return state;
}
